import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { Switch } from 'components/ui/switch';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { FlowDeviceConfig, useUpdateFlowConfig } from './useFlowConfig';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import firebase from 'firebase/compat/app';

interface LevelAlertsConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  config: FlowDeviceConfig;
}

export default function LevelAlertsConfigModal({
  isOpen,
  onClose,
  config,
}: LevelAlertsConfigModalProps) {
  const updateConfig = useUpdateFlowConfig();

  // Local state for toggles and values
  const [tempAlertEnabled, setTempAlertEnabled] = useState(false);
  const [tempAlertVal, setTempAlertVal] = useState<number | string>(0);
  const [tempAlertReset, setTempAlertReset] = useState<number | string>(0);

  const [lowLevelEnabled, setLowLevelEnabled] = useState(false);
  const [lowLevelLimit, setLowLevelLimit] = useState<number | string>(0);

  const [heatAlertEnabled, setHeatAlertEnabled] = useState(false);
  const [heatAlertMax, setHeatAlertMax] = useState<number | string>(0);
  const [heatAlertResetMax, setHeatAlertResetMax] = useState<number | string>(0);

  const [plantAlertsEnabled, setPlantAlertsEnabled] = useState(false);

  // Initialize state when config changes or modal opens
  useEffect(() => {
    if (isOpen && config) {
      setTempAlertEnabled(!!config.temp?.alert);
      setTempAlertVal(config.temp?.alert || 0);
      setTempAlertReset(config.temp?.reset || 0);

      setLowLevelEnabled(!!config.cmLimit);
      setLowLevelLimit(config.cmLimit || 0);

      setHeatAlertEnabled(!!config.temp?.alertMax);
      setHeatAlertMax(config.temp?.alertMax || 0);
      setHeatAlertResetMax(config.temp?.resetMax || 0);

      setPlantAlertsEnabled(!!config.plantAlerts);
    }
  }, [config, isOpen]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Build structured updates - backend will handle FieldValue.delete()
      const updates: {
        tempAlert?: { alert: number; reset: number } | null;
        heatAlert?: { alertMax: number; resetMax: number } | null;
        cmLimit?: number | null;
        plantAlerts: boolean;
      } = {
        plantAlerts: plantAlertsEnabled,
      };

      // Frost Alert
      if (tempAlertEnabled) {
        updates.tempAlert = {
          alert: Number(tempAlertVal),
          reset: Number(tempAlertReset),
        };
      } else {
        updates.tempAlert = null; // null means delete
      }

      // Heat Alert
      if (heatAlertEnabled) {
        updates.heatAlert = {
          alertMax: Number(heatAlertMax),
          resetMax: Number(heatAlertResetMax),
        };
      } else {
        updates.heatAlert = null;
      }

      // Low Level
      if (lowLevelEnabled) {
        updates.cmLimit = Number(lowLevelLimit);
      } else {
        updates.cmLimit = null;
      }

      await updateConfig.mutateAsync({ configId: config.id, data: updates as any });
      toast.success('Konfiguration opdateret');
      onClose();
    } catch (error) {
      console.error('Error updating config', error);
      toast.error('Kunne ikke opdatere konfiguration');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Konfigurer Alarmer</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSave} className="space-y-6">
          {/* Frost Alarm */}
          <div className="space-y-4 border-b pb-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="temp-alert" className="text-base font-semibold">
                Frost Alarm
              </Label>
              <Switch
                id="temp-alert"
                checked={tempAlertEnabled}
                onCheckedChange={setTempAlertEnabled}
              />
            </div>
            {tempAlertEnabled && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="tempAlertVal">Alarm ved (°C)</Label>
                  <Input
                    id="tempAlertVal"
                    type="number"
                    step="0.1"
                    value={tempAlertVal}
                    onChange={(e) => setTempAlertVal(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tempAlertReset">Nulstil ved (°C)</Label>
                  <Input
                    id="tempAlertReset"
                    type="number"
                    step="0.1"
                    value={tempAlertReset}
                    onChange={(e) => setTempAlertReset(e.target.value)}
                    required
                  />
                </div>
              </div>
            )}
          </div>

          {/* Low Level Alarm */}
          <div className="space-y-4 border-b pb-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="low-level" className="text-base font-semibold">
                Lav Niveau Alarm
              </Label>
              <Switch
                id="low-level"
                checked={lowLevelEnabled}
                onCheckedChange={setLowLevelEnabled}
              />
            </div>
            {lowLevelEnabled && (
              <div className="space-y-2">
                <Label htmlFor="lowLevelLimit">Alarm grænse (cm)</Label>
                <Input
                  id="lowLevelLimit"
                  type="number"
                  step="1"
                  value={lowLevelLimit}
                  onChange={(e) => setLowLevelLimit(e.target.value)}
                  required
                />
              </div>
            )}
          </div>

          {/* Heat Alarm */}
          <div className="space-y-4 border-b pb-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="heat-alert" className="text-base font-semibold">
                Varme Alarm
              </Label>
              <Switch
                id="heat-alert"
                checked={heatAlertEnabled}
                onCheckedChange={setHeatAlertEnabled}
              />
            </div>
            {heatAlertEnabled && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="heatAlertMax">Alarm ved (°C)</Label>
                  <Input
                    id="heatAlertMax"
                    type="number"
                    step="0.1"
                    value={heatAlertMax}
                    onChange={(e) => setHeatAlertMax(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="heatAlertResetMax">Nulstil ved (°C)</Label>
                  <Input
                    id="heatAlertResetMax"
                    type="number"
                    step="0.1"
                    value={heatAlertResetMax}
                    onChange={(e) => setHeatAlertResetMax(e.target.value)}
                    required
                  />
                </div>
              </div>
            )}
          </div>

          {/* Plant Alerts */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="plant-alerts" className="text-base font-semibold">
                Anlæg Alarmer
              </Label>
              <Switch
                id="plant-alerts"
                checked={plantAlertsEnabled}
                onCheckedChange={setPlantAlertsEnabled}
              />
            </div>
            <p className="text-sm text-gray-500">
              Aktiverer generelle anlægsalarmer (f.eks. ved fejl på anlæg).
            </p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Annuller
            </Button>
            <Button type="submit" disabled={updateConfig.isPending}>
              {updateConfig.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Gem
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
